import {
    useCallback,
    useEffect,
    useState
} from "react";
import {
    ActivityIndicator,
    TouchableOpacity,
    View
} from "react-native";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    PageContainer,
    TextInput,
    Sticker,
    Button,
    Text
} from "ncore-ui-kit";
import {
    useFocusEffect,
    useNavigation
} from "@react-navigation/native";
import type {
    InstagramInitialData
} from "../../../../navigation/type";
import type RootStackParamList from "../../../../navigation/type";
import type {
    NewsPayloadType
} from "../../../../services/restAPI/actions/getAllNews/type";
import {
    type NativeStackNavigationProp
} from "@react-navigation/native-stack";
import {
    SearchIcon,
    PlusIcon,
    PenIcon
} from "lucide-react-native";
import AsyncStorage from "@react-native-async-storage/async-storage";
import Svg, {
    Circle,
    Path,
    Rect
} from "react-native-svg";
import {
    setInstagramDraft
} from "../../../../utils/instagramDraft";
import FetchInstagramModal from "../../../../components/fetchInstagramModal";
import ShareInstagramModal from "../../../../components/shareInstagramModal";
import RESTServiceContext from "../../../../services/restAPI/context";
import SmartCoverImage from "../../../../components/smartCoverImage";
import Pagination from "../../../../components/pagination";

const CustomInstagramIcon = ({
    color = "#E1306C",
    size = 20
}: {
    color?: string;
    size?: number;
}) => {
    return <Svg
        strokeLinejoin="round"
        strokeLinecap="round"
        viewBox="0 0 24 24"
        strokeWidth={2}
        stroke={color}
        height={size}
        width={size}
        fill="none"
    >
        <Rect
            height={20}
            width={20}
            rx={5}
            ry={5}
            x={2}
            y={2}
        />
        <Path
            d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"
        />
        <Circle
            fill={color}
            cx={17.5}
            cy={6.5}
            r={1.5}
        />
    </Svg>;
};

const resolvePhotoUrl = (
    photo?: { path?: string; url?: string; } | string,
    authToken?: string
): string => {
    if(!photo) {
        return "";
    }

    let targetUrl = "";

    if(typeof photo === "string") {
        targetUrl = photo;
    } else if(photo.url && photo.url.startsWith("http")) {
        targetUrl = photo.url;
    } else if(photo.path) {
        targetUrl = photo.path;
    }

    if(!targetUrl) {
        return "";
    }

    if(targetUrl.includes("/uploads/drive/")) {
        targetUrl = targetUrl.replace("/uploads/drive/", "/drive/");
    }

    const isDrive = targetUrl.includes("/drive/");
    if(isDrive) {
        if(!targetUrl.startsWith("http")) {
            const cleanPath = targetUrl.startsWith("/") ? targetUrl : `/${targetUrl}`;
            targetUrl = `${process.env.STORAGE_URL}${cleanPath}`;
        }
        if(authToken && !targetUrl.includes("token=")) {
            const separator = targetUrl.includes("?") ? "&" : "?";
            targetUrl = `${targetUrl}${separator}token=${authToken}`;
        }
        return targetUrl;
    }

    if(targetUrl.startsWith("http") || targetUrl.startsWith("blob:")) {
        return targetUrl;
    }

    const cleanPath = targetUrl.startsWith("/uploads")
        ? targetUrl
        : `/uploads${targetUrl.startsWith("/") ? "" : "/"}${targetUrl}`;

    return `${process.env.STORAGE_URL}${cleanPath}`;
};

const NewsList = () => {
    const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();

    const [
        newsList,
        setNewsList
    ] = useState<NewsPayloadType[]>([]);

    const [
        page,
        setPage
    ] = useState<number>(1);

    const [
        totalCount,
        setTotalCount
    ] = useState<number>(0);

    const [
        loading,
        setLoading
    ] = useState<boolean>(true);

    const [
        isInstagramModalVisible,
        setIsInstagramModalVisible
    ] = useState<boolean>(false);

    const [
        selectedNewsForShare,
        setSelectedNewsForShare
    ] = useState<NewsPayloadType | null>(null);

    const [
        authToken,
        setAuthToken
    ] = useState<string>("");

    const [
        searchQuery,
        setSearchQuery
    ] = useState<string>("");

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const {
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    useEffect(() => {
        AsyncStorage.getItem("authToken").then((token: string | null) => {
            if(token) {
                setAuthToken(token);
            }
        });
    }, []);

    const handleInstagramSuccess = (initialData: InstagramInitialData) => {
        setInstagramDraft(initialData);
        navigation.navigate("NewsCreate");
    };

    const fetchNews = useCallback(async (currentPage: number) => {
        try {
            setLoading(true);
            const requestParams: Record<string, string | string[] | number | boolean> = {
                page: currentPage
            };
            if (searchQuery) {
                requestParams.search = searchQuery;
            }
            const result = await RESTServiceContext.action("getAllNews", requestParams);
            if(result.payload) {
                setNewsList(result.payload as unknown as NewsPayloadType[]);
                if (result.totalCount !== undefined) {
                    setTotalCount(result.totalCount);
                }
            } else {
                setNewsList([]);
                setTotalCount(0);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    }, [
        searchQuery
    ]);

    useFocusEffect(
        useCallback(() => {
            fetchNews(1);
            setPage(1);
        }, [
            fetchNews
        ])
    );

    const renderHeader = () => {
        return <View
            style={[
                stylesheet.headerContainer,
                {
                    gap: spaces.spacingMd
                }
            ]}
        >
            <Text
                variant="titleLargeSize"
            >
                {localize("menu-news-title")}
            </Text>
            <View
                style={[
                    stylesheet.headerActions,
                    {
                        gap: spaces.spacingMd
                    }
                ]}
            >
                <Button
                    onPress={() => setIsInstagramModalVisible(true)}
                    customTextColor={colors.project.brandWhite}
                    title={localize("fetch-from-instagram")}
                    customBorderColor="secondaryButton"
                    customColor="secondaryButton"
                    spreadBehaviour="free"
                    variant="outline"
                />
                <Button
                    onPress={() => navigation.navigate("NewsCreate")}
                    customTextColor={colors.project.brandWhite}
                    icon={({
                        color,
                        size
                    }) => <PlusIcon
                        color={colors.content.icon[color]}
                        size={size}
                    />}
                    title={localize("news-create-title")}
                    customBorderColor="gold"
                    spreadBehaviour="free"
                    customColor="gold"
                />
            </View>
        </View>;
    };

    const renderFilters = () => {
        return <View
            style={[
                stylesheet.filterSection,
                {
                    marginTop: spaces.spacingLg,
                    gap: spaces.spacingMd
                }
            ]}
        >
            <View
                style={stylesheet.searchContainer}
            >
                <TextInput
                    placeholder={localize("news-search-placeholder")}
                    customColor={colors.project.brandWhite}
                    icon={({
                        color,
                        size
                    }) => <SearchIcon
                        color={colors.content.icon[color]}
                        size={size}
                    />}
                    onChangeText={setSearchQuery}
                    spreadBehaviour="stretch"
                    value={searchQuery}
                />
            </View>
        </View>;
    };

    const renderCard = (
        newsItem: NewsPayloadType,
        index: number
    ) => {
        const rawCover = newsItem.coverPhotoPath || (newsItem.postPhotos && newsItem.postPhotos[0]?.path ? newsItem.postPhotos[0].path : "");
        const coverUrl = resolvePhotoUrl(rawCover, authToken);
        const shortContent = newsItem.shortContent || (newsItem.data && newsItem.data[0] ? newsItem.data[0].shortContent : "");
        const title = newsItem.title || (newsItem.data && newsItem.data[0] ? newsItem.data[0].title : "-");
        const newsKey = newsItem._id || `news-${index}`;

        return <View
            style={stylesheet.cardWrapper}
            key={newsKey}
        >
            <TouchableOpacity
                style={[
                    stylesheet.cardContainer,
                    {
                        backgroundColor: colors.project.brandWhite,
                        borderColor: colors.project.brandBorder,
                        borderWidth: borders.seperator,
                        borderRadius: radiuses.md
                    }
                ]}
                onPress={() => {
                    navigation.navigate("NewsDetail", {
                        newsID: newsItem._id
                    });
                }}
                activeOpacity={0.7}
            >
                <View
                    style={[
                        stylesheet.cardBanner,
                        {
                            backgroundColor: colors.project.companyBannerBackground || "#335C57"
                        }
                    ]}
                >
                    {
                        coverUrl ?
                            <SmartCoverImage
                                containerStyle={stylesheet.bannerImage}
                                url={coverUrl}
                            />
                        :
                            null
                    }
                    <TouchableOpacity
                        style={[
                            stylesheet.cardInstagramButton,
                            {
                                backgroundColor: (newsItem.instagramPostUrl || newsItem.instagramPostId) ? "rgba(225, 48, 108, 0.25)" : colors.system.scrim,
                                borderColor: (newsItem.instagramPostUrl || newsItem.instagramPostId) ? "#E1306C" : colors.project.brandWhite,
                                borderRadius: radiuses.full,
                                borderWidth: borders.line,
                                left: spaces.spacingMd,
                                top: spaces.spacingMd
                            }
                        ]}
                        onPress={(e) => {
                            e.stopPropagation();
                            setSelectedNewsForShare(newsItem);
                        }}
                        activeOpacity={0.8}
                    >
                        <CustomInstagramIcon
                            color={(newsItem.instagramPostUrl || newsItem.instagramPostId) ? "#E1306C" : colors.project.brandWhite}
                            size={18}
                        />
                    </TouchableOpacity>
                    <View
                        style={[
                            stylesheet.cardTopRightBadges,
                            {
                                right: spaces.spacingMd,
                                gap: spaces.spacingSm,
                                top: spaces.spacingMd
                            }
                        ]}
                    >
                        {
                            newsItem.isActive ?
                                <Sticker
                                    title={localize("news-active-badge")}
                                    spreadBehaviour="free"
                                    type="success"
                                    size="small"
                                />
                            :
                                null
                        }
                        {
                            newsItem.isFeatured ?
                                <Sticker
                                    title={localize("news-featured-badge") || "Öne Çıkan"}
                                    spreadBehaviour="free"
                                    type="warning"
                                    size="small"
                                />
                            :
                                null
                        }
                    </View>
                </View>
                <View
                    style={[
                        stylesheet.cardBody,
                        {
                            paddingHorizontal: spaces.spacingMd,
                            paddingBottom: spaces.spacingMd,
                            paddingTop: spaces.spacingXl,
                            gap: spaces.spacingSm
                        }
                    ]}
                >
                    <View
                        style={stylesheet.cardContentTop}
                    >
                        <Text
                            style={stylesheet.cardTitle}
                            variant="titleMediumSize"
                            numberOfLines={2}
                        >
                            {title}
                        </Text>
                        {
                            shortContent ?
                                <Text
                                    style={[
                                        stylesheet.cardDescription,
                                        {
                                            color: colors.content.text.mid
                                        }
                                    ]}
                                    variant="bodyMediumSize"
                                    numberOfLines={2}
                                >
                                    {shortContent}
                                </Text>
                            :
                                null
                        }
                    </View>
                    <View
                        style={[
                            stylesheet.cardFooter,
                            {
                                marginTop: spaces.spacingMd,
                                gap: spaces.spacingSm
                            }
                        ]}
                    >
                        <Button
                            icon={({
                                color,
                                size
                            }) => <PenIcon
                                color={colors.content.icon[color]}
                                size={size}
                            />}
                            onPress={() => {
                                navigation.navigate("NewsEdit", {
                                    newsID: newsItem._id
                                });
                            }}
                            title={localize("news-edit-title")}
                            customBorderColor="secondaryButton"
                            customColor="secondaryButton"
                            spreadBehaviour="stretch"
                            variant="filled"
                        />
                    </View>
                </View>
            </TouchableOpacity>
        </View>;
    };

    const renderPagination = () => {
        if (totalCount === 0) {
            return null;
        }

        return <View
            style={[
                stylesheet.paginationContainer,
                {
                    marginTop: spaces.spacingLg
                }
            ]}
        >
            <Pagination
                onSelect={(item) => {
                    setPage(item.pageNumber);
                    fetchNews(item.pageNumber);
                }}
                totalDataCount={totalCount}
                currentPage={page}
                itemPerPage={20}
            />
        </View>;
    };

    const renderContent = () => {
        if(loading && newsList.length === 0) {
            return <View
                style={[
                    stylesheet.emptyContainer,
                    {
                        padding: spaces.spacingXl
                    }
                ]}
            >
                <ActivityIndicator
                    color={colors.project.gold}
                    size="large"
                />
            </View>;
        }

        if(newsList.length === 0) {
            return <View
                style={[
                    stylesheet.emptyContainer,
                    {
                        padding: spaces.spacingXl
                    }
                ]}
            >
                <Text
                    style={[
                        stylesheet.emptyText,
                        {
                            color: colors.content.text.mid
                        }
                    ]}
                    variant="bodyLargeSize"
                >
                    {localize("news-empty-list")}
                </Text>
            </View>;
        }

        return <View>
            <View
                style={[
                    stylesheet.gridContainer,
                    {
                        marginTop: spaces.spacingLg,
                        gap: spaces.spacingLg
                    }
                ]}
            >
                {newsList.map(renderCard)}
            </View>
            {renderPagination()}
        </View>;
    };

    return <PageContainer
        isScrollable={true}
    >
        <View
            style={[
                stylesheet.container,
                {
                    padding: spaces.spacingXl
                }
            ]}
        >
            {renderHeader()}
            {renderFilters()}
            {renderContent()}
        </View>
        <FetchInstagramModal
            onClose={() => setIsInstagramModalVisible(false)}
            isVisible={isInstagramModalVisible}
            onSuccess={handleInstagramSuccess}
        />
        {
            selectedNewsForShare ?
                <ShareInstagramModal
                    isAlreadyPublished={Boolean(selectedNewsForShare.instagramPostUrl || selectedNewsForShare.instagramPostId)}
                    shortContent={selectedNewsForShare.shortContent || selectedNewsForShare.data?.[0]?.shortContent}
                    title={selectedNewsForShare.title || selectedNewsForShare.data?.[0]?.title || ""}
                    onSuccess={(url, id) => {
                        setNewsList(prev => prev.map(item => {
                            if(item._id === selectedNewsForShare._id) {
                                return {
                                    ...item,
                                    instagramPublishedAt: new Date().toISOString(),
                                    instagramPostUrl: url,
                                    instagramPostId: id
                                };
                            }
                            return item;
                        }));
                        setSelectedNewsForShare(null);
                    }}
                    coverPhotoPath={selectedNewsForShare.coverPhotoPath}
                    onClose={() => setSelectedNewsForShare(null)}
                    postPhotos={selectedNewsForShare.postPhotos}
                    availableData={selectedNewsForShare.data}
                    isVisible={Boolean(selectedNewsForShare)}
                    targetID={selectedNewsForShare._id}
                    targetType="news"
                />
            :
                null
        }
    </PageContainer>;
};

export default NewsList;
